import { useEffect, useMemo } from 'react';
import { get } from 'lodash';
import { createFilterOptions } from '@material-ui/lab/useAutocomplete';
import { useForm, useFormState } from 'react-final-form';
import QCXAutocompleteSelect from '../../autocomplete-select/QCXAutocompleteSelect';
import type { AtributoCampoProps } from './types';

type SelectOption = {
  id: string;
  value: string;
};

export default function DominioDinamicoV2({
  atributo,
  basePath,
  valuesPath,
  combinacoesValidas = [],
  disabled = false,
}: AtributoCampoProps) {
  const { values } = useFormState();
  const form = useForm();
  const name = `${basePath}.${atributo.codigo}.valor`;
  const label = `${atributo.descricaoCodigo} (${atributo.codigo})`;
  const filterOptions = createFilterOptions<SelectOption>({ stringify: (item) => `${item.id} - ${item.value}` });
  const valoresAtributos = get(values, valuesPath || 'ignorableFields.tratamentosTtce.metadados.valores', {});
  const valorAtual = valoresAtributos?.[atributo.codigo]?.valor;

  const opcoesDisponiveis = useMemo(() => {
    if (!combinacoesValidas.length) return atributo.dominio;

    const valoresFormulario = (get(values, valuesPath || 'ignorableFields.tratamentosTtce.metadados.valores', {}) || {}) as Record<
      string,
      { valor?: unknown }
    >;
    const outrosAtributosSelecionados = Object.entries(valoresFormulario)
      .filter(
        ([codigo, valor]) =>
          codigo !== atributo.codigo && valor?.valor !== undefined && valor?.valor !== null && valor?.valor !== ''
      )
      .map(([codigo, valor]) => ({ codigo, valor: String(valor.valor) }));

    const combinacoesCompativeis = combinacoesValidas.filter((combinacao) =>
      outrosAtributosSelecionados.every(({ codigo, valor }) => {
        const atributoNaCombinacao = combinacao.find((item) => item.codigo === codigo);
        return (
          !atributoNaCombinacao ||
          atributoNaCombinacao.valor === valor ||
          atributoNaCombinacao.valor === ''
        );
      })
    );

    if (!combinacoesCompativeis.length) return atributo.dominio;

    const valoresPossiveis = new Set<string>();
    let possuiCombinacaoSemAtributo = false;

    combinacoesCompativeis.forEach((combinacao) => {
      const atributoNaCombinacao = combinacao.find((item) => item.codigo === atributo.codigo);

      if (atributoNaCombinacao) {
        valoresPossiveis.add(atributoNaCombinacao.valor);
      } else {
        possuiCombinacaoSemAtributo = true;
      }
    });

    if (possuiCombinacaoSemAtributo && valoresPossiveis.size === 0) return [];

    const opcoesFiltradas = atributo.dominio.filter((opcao) => valoresPossiveis.has(opcao.valor));
    const opcoes = opcoesFiltradas.length ? opcoesFiltradas : atributo.dominio;
    const opcaoAtual = atributo.dominio.find((opcao) => opcao.valor === valorAtual);

    if (opcaoAtual && !opcoes.some((opcao) => opcao.valor === valorAtual)) {
      return [...opcoes, opcaoAtual];
    }

    return opcoes;
  }, [atributo, combinacoesValidas, valorAtual, values, valuesPath]);

  const options = useMemo(
    () =>
      opcoesDisponiveis.map((opcao): SelectOption => ({
        id: opcao.valor,
        value: opcao.descricao,
      })),
    [opcoesDisponiveis]
  );

  const defaultValue = useMemo(
    () => options.find((opcao) => opcao.id === valorAtual) ?? { id: null, value: '' },
    [options, valorAtual]
  );

  useEffect(() => {
    if (valorAtual && !opcoesDisponiveis.some((opcao) => opcao.valor === valorAtual)) {
      form.change(name, null);
    }
  }, [atributo.codigo, form, name, opcoesDisponiveis, values]);

  if (!options.length) return null;

  return (
    <QCXAutocompleteSelect
      label={label}
      id={name}
      name={name}
      options={options}
      defaultValue={defaultValue}
      filterOptions={filterOptions}
      getOptionLabel={(item: SelectOption) => (item.value ? `${item.id} - ${item.value}` : '')}
      required
      disabled={disabled}
      isConsult={disabled}
    />
  );
}
